import useTranslation from "next-translate/useTranslation";
import { AlertTriangle, CheckCircle, type LucideIcon, Rocket } from "lucide-react";

interface StateMessageProps {
  readonly variant: "error" | "sending" | "success";
  readonly propMessage?: string;
}

export default function StateMessage(props: StateMessageProps) {
  const { variant, propMessage } = props;
  const { t } = useTranslation("common");

  // Configuración por variante
  const configs: Record<
    StateMessageProps["variant"],
    {
      icon: LucideIcon;
      iconColor: string;
      bgColor: string;
      borderColor: string;
      title: string;
      subtitle: string;
      animation: string;
    }
  > = {
    error: {
      icon: AlertTriangle,
      iconColor: "text-red-500",
      bgColor: "bg-red-50 dark:bg-red-900/20",
      borderColor: "border-red-200 dark:border-red-800",
      title: propMessage || t("Error"),
      subtitle: t("Por favor intenta nuevamente"),
      animation: "animate-shake"
    },
    sending: {
      icon: Rocket,
      iconColor: "text-blue-500",
      bgColor: "bg-blue-50 dark:bg-blue-900/20",
      borderColor: "border-blue-200 dark:border-blue-800",
      title: t("Enviando"),
      subtitle: t("Reconocimiento") + "...",
      animation: "animate-bounce"
    },
    success: {
      icon: CheckCircle,
      iconColor: "text-green-500",
      bgColor: "bg-green-50 dark:bg-green-900/20",
      borderColor: "border-green-200 dark:border-green-800",
      title: t("SENDED_RECOGNITION_MESSAGE"),
      subtitle: t("Tu reconocimiento ha sido enviado exitosamente"),
      animation: "animate-scale-in"
    }
  };

  const config = configs[variant];
  const IconComp = config.icon;

  return (
    <div
      className={`${config.bgColor} ${config.borderColor} rounded-lg border-2 p-8 transition-all duration-300`}
    >
      <div className="flex flex-col items-center space-y-4">
        {/* Icon with animation */}
        <div
          className={`${config.animation} flex h-20 w-20 items-center justify-center rounded-full ${config.bgColor} shadow-lg`}
        >
          <IconComp className={`h-10 w-10 ${config.iconColor}`} />
        </div>

        {/* Title */}
        <h3 className={`text-center text-xl font-bold ${config.iconColor}`}>
          {config.title}
        </h3>

        {/* Subtitle */}
        {variant === "sending" ? (
          <div className="flex items-center space-x-2">
            <div className="flex space-x-1">
              <span
                className="h-2 w-2 animate-bounce rounded-full bg-blue-500"
                style={{ animationDelay: "0ms" }}
              ></span>
              <span
                className="h-2 w-2 animate-bounce rounded-full bg-blue-500"
                style={{ animationDelay: "150ms" }}
              ></span>
              <span
                className="h-2 w-2 animate-bounce rounded-full bg-blue-500"
                style={{ animationDelay: "300ms" }}
              ></span>
            </div>
            <span className="text-center text-sm text-slate-600 dark:text-zinc-400">
              {config.subtitle}
            </span>
          </div>
        ) : (
          <p className="text-center text-sm text-slate-600 dark:text-zinc-400">
            {config.subtitle}
          </p>
        )}

        {/* Progress bar for sending */}
        {variant === "sending" && (
          <div className="w-full max-w-xs">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-blue-100 dark:bg-blue-900/30">
              <div className="animate-progress h-full rounded-full bg-blue-500"></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
